import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./contexts/ThemeContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import OwnerDashboard from "./pages/OwnerDashboard";
import BoardDashboard from "./pages/BoardDashboard";
import DocumentLibrary from "./pages/DocumentLibrary";
import ViolationsPage from "./pages/ViolationsPage";
import DRCPage from "./pages/DRCPage";
import PropertiesPage from "./pages/PropertiesPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import OwnersPage from "./pages/OwnersPage";
import AIAssistant from "./pages/AIAssistant";
import CorrespondencePage from "./pages/CorrespondencePage";
import AdminAISettings from "./pages/AdminAISettings";
import MeetingsPage from "./pages/MeetingsPage";
import PaymentsPage from "./pages/PaymentsPage";
import ForumPage from "./pages/ForumPage";
import OwnerProfilePage from "./pages/OwnerProfilePage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import GISPage from "./pages/GISPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import AccountantDashboard from "./pages/AccountantDashboard";
import PropertyManagerDashboard from "./pages/PropertyManagerDashboard";
import EmergencyAlertsPage from "./pages/EmergencyAlertsPage";
import VendorManagementPage from "./pages/VendorManagementPage";
import BudgetingPage from "./pages/BudgetingPage";
import OwnerPortal2 from "./pages/OwnerPortal2";
import EventsPage from "./pages/EventsPage";
import LegalCompliancePage from "./pages/LegalCompliancePage";
import MetroDistrictPage from "./pages/MetroDistrictPage";
import ListingToolkitPage from "./pages/ListingToolkitPage";
import AutomationDashboardPage from "./pages/AutomationDashboardPage";
import CommunicationsHubPage from "./pages/CommunicationsHubPage";
import WorkflowHubPage from "./pages/WorkflowHubPage";
import AILogsPage from "./pages/AILogsPage";
import BackupsPage from "./pages/BackupsPage";
import BulkGISImportPage from "./pages/BulkGISImportPage";
import SiteSettingsPage from "./pages/SiteSettingsPage";
import SiteSettingsPageNew from "./pages/SiteSettingsPageNew";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import KnowledgeBasePageRAG from "./pages/KnowledgeBasePageRAG";
import ProactiveAgentPage from "./pages/ProactiveAgentPage";
import ExportAuditLogsPage from "./pages/ExportAuditLogsPage";
import ComplaintsPage from "./pages/ComplaintsPage";
import ComplaintAnalyticsDashboard from "./pages/ComplaintAnalyticsDashboard";
import IntegrationSettingsPage from "./pages/IntegrationSettingsPage";
import GovernanceNoticesPage from "./pages/GovernanceNoticesPage";
import OwnerVerificationPage from "./pages/OwnerVerificationPage";
import DRCSubmissionPage from "./pages/DRCSubmissionPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import CommunicationsUnifiedPage from "./pages/CommunicationsUnifiedPage";
import EnforcementUnifiedPage from "./pages/EnforcementUnifiedPage";
import CommunityReportPage from "./pages/CommunityReportPage";
import CommunityReportsAdmin from "./pages/CommunityReportsAdmin";
import AdminSidebar from "./components/layout/AdminSidebar";

// Components
import FloatingAIChat from "./components/FloatingAIChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api/v1`;

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
            withCredentials: true
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = "owner") => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, setToken, loading, login, register, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    const roleRedirects = {
      owner: "/owner",
      board: "/board",
      drc: "/board",
      admin: "/admin",
      accountant: "/accountant",
      property_manager: "/property-manager",
      guest: "/"
    };
    return <Navigate to={roleRedirects[user.role] || "/owner"} replace />;
  }

  return children;
};

// Helper function to get redirect path based on role
const getRoleRedirectPath = (role) => {
  const roleRedirects = {
    owner: "/owner",
    board: "/board",
    drc: "/board",
    admin: "/admin",
    accountant: "/accountant",
    property_manager: "/property-manager",
    guest: "/"
  };
  return roleRedirects[role] || "/owner";
};

// Auth Callback Handler
const AuthCallback = () => {
  const { setUser, setToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const hash = location.hash;
      const sessionId = hash.includes('session_id=') 
        ? hash.split('session_id=')[1]?.split('&')[0] 
        : null;

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        const { user: userData, session_token } = response.data;
        
        localStorage.setItem("token", session_token);
        setToken(session_token);
        setUser(userData);
        
        // Redirect based on role
        navigate(getRoleRedirectPath(userData.role), { state: { user: userData } });
      } catch (error) {
        console.error("Auth callback error:", error);
        navigate('/login');
      }
    };

    processCallback();
  }, [location, navigate, setUser, setToken]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse text-muted-foreground">Authenticating...</div>
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Owner Routes */}
      <Route path="/owner" element={
        <ProtectedRoute>
          <OwnerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/owner/dashboard" element={
        <ProtectedRoute>
          <OwnerPortal2 />
        </ProtectedRoute>
      } />
      <Route path="/owner/events" element={
        <ProtectedRoute>
          <EventsPage />
        </ProtectedRoute>
      } />
      <Route path="/owner/reservations" element={
        <ProtectedRoute>
          <EventsPage />
        </ProtectedRoute>
      } />
      <Route path="/owner/violations" element={
        <ProtectedRoute>
          <ViolationsPage />
        </ProtectedRoute>
      } />
      <Route path="/owner/drc" element={
        <ProtectedRoute>
          <DRCPage />
        </ProtectedRoute>
      } />
      <Route path="/owner/drc/submit" element={
        <ProtectedRoute>
          <DRCSubmissionPage />
        </ProtectedRoute>
      } />
      <Route path="/owner/report-concern" element={
        <ProtectedRoute>
          <CommunityReportPage />
        </ProtectedRoute>
      } />
      <Route path="/board/drc/submit" element={
        <ProtectedRoute allowedRoles={["admin", "board", "drc"]}>
          <DRCSubmissionPage />
        </ProtectedRoute>
      } />
      <Route path="/owner/documents" element={
        <ProtectedRoute>
          <DocumentLibrary />
        </ProtectedRoute>
      } />
      
      {/* Board/Admin Routes */}
      <Route path="/board" element={
        <ProtectedRoute allowedRoles={["admin", "board", "drc"]}>
          <BoardDashboard />
        </ProtectedRoute>
      } />
      <Route path="/board/documents" element={
        <ProtectedRoute allowedRoles={["admin", "board", "drc"]}>
          <DocumentLibrary isBoard={true} />
        </ProtectedRoute>
      } />
      <Route path="/board/violations" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <ViolationsPage isBoard={true} />
        </ProtectedRoute>
      } />
      <Route path="/board/community-reports" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <CommunityReportsAdmin />
        </ProtectedRoute>
      } />
      <Route path="/board/drc" element={
        <ProtectedRoute allowedRoles={["admin", "board", "drc"]}>
          <DRCPage isBoard={true} />
        </ProtectedRoute>
      } />
      <Route path="/board/properties" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <PropertiesPage />
        </ProtectedRoute>
      } />
      <Route path="/board/properties/:propertyId" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <PropertyDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/board/owners" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <OwnersPage />
        </ProtectedRoute>
      } />
      <Route path="/board/ai" element={
        <ProtectedRoute allowedRoles={["admin", "board", "drc"]}>
          <AIAssistant />
        </ProtectedRoute>
      } />
      <Route path="/board/correspondence" element={
        <ProtectedRoute allowedRoles={["admin", "board", "drc"]}>
          <CorrespondencePage />
        </ProtectedRoute>
      } />
      <Route path="/board/communications-hub" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <AdminSidebar>
            <CommunicationsUnifiedPage />
          </AdminSidebar>
        </ProtectedRoute>
      } />
      <Route path="/board/enforcement" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <AdminSidebar>
            <EnforcementUnifiedPage />
          </AdminSidebar>
        </ProtectedRoute>
      } />
      <Route path="/board/admin/ai-settings" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <AdminAISettings />
        </ProtectedRoute>
      } />
      <Route path="/board/meetings" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <MeetingsPage />
        </ProtectedRoute>
      } />
      <Route path="/board/payments" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <PaymentsPage isBoard={true} />
        </ProtectedRoute>
      } />
      
      {/* Owner payment route */}
      <Route path="/owner/payments" element={
        <ProtectedRoute>
          <PaymentsPage isBoard={false} />
        </ProtectedRoute>
      } />
      <Route path="/owner/forum" element={
        <ProtectedRoute>
          <ForumPage isBoard={false} />
        </ProtectedRoute>
      } />
      <Route path="/owner/profile" element={
        <ProtectedRoute>
          <OwnerProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Board Forum route */}
      <Route path="/board/forum" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <ForumPage isBoard={true} />
        </ProtectedRoute>
      } />
      
      {/* GIS Map routes */}
      <Route path="/board/gis" element={
        <ProtectedRoute allowedRoles={["admin", "board", "drc"]}>
          <GISPage />
        </ProtectedRoute>
      } />
      <Route path="/owner/gis" element={
        <ProtectedRoute>
          <GISPage />
        </ProtectedRoute>
      } />
      
      {/* Announcements routes */}
      <Route path="/board/announcements" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <AnnouncementsPage isBoard={true} />
        </ProtectedRoute>
      } />
      <Route path="/owner/announcements" element={
        <ProtectedRoute>
          <AnnouncementsPage isBoard={false} />
        </ProtectedRoute>
      } />
      
      {/* Emergency Alerts routes */}
      <Route path="/emergency" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <EmergencyAlertsPage />
        </ProtectedRoute>
      } />
      <Route path="/board/emergency" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <EmergencyAlertsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/emergency" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <EmergencyAlertsPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Dashboard route */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-logs" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager", "board"]}>
          <AILogsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/backups" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <BackupsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/site-settings" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <SiteSettingsPageNew />
        </ProtectedRoute>
      } />
      <Route path="/board/gis/bulk-import" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <BulkGISImportPage />
        </ProtectedRoute>
      } />
      
      {/* Knowledge Base & Help Center */}
      <Route path="/help" element={<KnowledgeBasePage />} />
      <Route path="/owner/help" element={
        <ProtectedRoute allowedRoles={["owner", "board", "admin", "property_manager", "accountant"]}>
          <KnowledgeBasePage />
        </ProtectedRoute>
      } />
      <Route path="/board/help" element={
        <ProtectedRoute allowedRoles={["board", "admin", "property_manager"]}>
          <KnowledgeBasePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/knowledge-base" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager"]}>
          <KnowledgeBasePage isAdmin={true} />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-knowledge-base" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <KnowledgeBasePageRAG />
        </ProtectedRoute>
      } />
      <Route path="/admin/proactive-agent" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <ProactiveAgentPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/export" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <ExportAuditLogsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/complaints" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <ComplaintsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/complaints/analytics" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <ComplaintAnalyticsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/integrations" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <IntegrationSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/governance-notices" element={
        <ProtectedRoute allowedRoles={["admin", "board"]}>
          <GovernanceNoticesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/owner-verification" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <OwnerVerificationPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      
      {/* Vendor Management routes */}
      <Route path="/vendors" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager", "accountant"]}>
          <VendorManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/board/vendors" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <VendorManagementPage />
        </ProtectedRoute>
      } />
      
      {/* Events & Reservations routes */}
      <Route path="/events" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <EventsPage />
        </ProtectedRoute>
      } />
      <Route path="/board/events" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <EventsPage />
        </ProtectedRoute>
      } />
      
      {/* Legal & Compliance routes (Block 23) */}
      <Route path="/legal" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <LegalCompliancePage />
        </ProtectedRoute>
      } />
      <Route path="/board/legal" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <LegalCompliancePage />
        </ProtectedRoute>
      } />
      
      {/* Metro District routes (Block 24) */}
      <Route path="/metro" element={
        <ProtectedRoute>
          <MetroDistrictPage />
        </ProtectedRoute>
      } />
      <Route path="/board/metro" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <MetroDistrictPage />
        </ProtectedRoute>
      } />
      
      {/* Listing Toolkit routes (Block 25) */}
      <Route path="/listing" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager", "accountant"]}>
          <ListingToolkitPage />
        </ProtectedRoute>
      } />
      <Route path="/board/listing" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <ListingToolkitPage />
        </ProtectedRoute>
      } />
      
      {/* Automation Engine routes (Block 26) */}
      <Route path="/automation" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager"]}>
          <AutomationDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/board/automation" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager"]}>
          <AutomationDashboardPage />
        </ProtectedRoute>
      } />
      
      {/* Communications Hub routes (Block 27) */}
      <Route path="/communications" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager", "board"]}>
          <CommunicationsHubPage />
        </ProtectedRoute>
      } />
      <Route path="/board/communications" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager", "board"]}>
          <CommunicationsHubPage />
        </ProtectedRoute>
      } />
      
      {/* Workflow Hub routes (Block 28) */}
      <Route path="/workflow" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager", "board", "accountant"]}>
          <WorkflowHubPage />
        </ProtectedRoute>
      } />
      <Route path="/board/workflow" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager", "board", "accountant"]}>
          <WorkflowHubPage />
        </ProtectedRoute>
      } />
      
      {/* Budgeting & Financial Planning routes */}
      <Route path="/budgets" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager", "accountant"]}>
          <BudgetingPage />
        </ProtectedRoute>
      } />
      <Route path="/board/budgets" element={
        <ProtectedRoute allowedRoles={["admin", "board", "property_manager"]}>
          <BudgetingPage />
        </ProtectedRoute>
      } />
      <Route path="/accountant/budgets" element={
        <ProtectedRoute allowedRoles={["admin", "accountant", "property_manager"]}>
          <BudgetingPage />
        </ProtectedRoute>
      } />
      
      {/* Accountant Dashboard route */}
      <Route path="/accountant" element={
        <ProtectedRoute allowedRoles={["admin", "accountant", "property_manager"]}>
          <AccountantDashboard />
        </ProtectedRoute>
      } />
      <Route path="/accountant/*" element={
        <ProtectedRoute allowedRoles={["admin", "accountant", "property_manager"]}>
          <AccountantDashboard />
        </ProtectedRoute>
      } />
      
      {/* Property Manager Dashboard route */}
      <Route path="/property-manager" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager"]}>
          <PropertyManagerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/property-manager/*" element={
        <ProtectedRoute allowedRoles={["admin", "property_manager"]}>
          <PropertyManagerDashboard />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <div className="min-h-screen bg-background noise-bg">
            <AppRouter />
            <FloatingAIChat />
            <Toaster position="top-right" />
          </div>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
